import { useEffect, useRef, useState } from "react";
import { streamAsk, api } from "@/lib/api";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Sparkle, ArrowUp, Flag } from "lucide-react";
import { cn } from "@/lib/utils";

const SESSION = "gm-jake-session";

export default function AskScratch({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/ask-scratch/suggestions").then(({ data }) => setSuggestions(data.suggestions));
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamAsk(
        msg,
        SESSION,
        (delta) =>
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
            return copy;
          }),
        () => setBusy(false)
      );
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the analyst just now." };
        return copy;
      });
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col backdrop-blur-2xl bg-white/95 border-l border-[#E5E7EB]"
        data-testid="ask-scratch-panel"
      >
        <div className="px-6 h-16 flex items-center gap-3 border-b border-[#E5E7EB] shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#0F3F2D] flex items-center justify-center">
            <Sparkle size={16} className="text-[#C7A24A]" />
          </div>
          <div>
            <div className="font-display font-semibold text-[#1C1F1E] leading-tight">Ask Scratch</div>
            <div className="text-xs text-[#52525B] flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#059669] pulse-dot" />
              Your AI operating analyst
            </div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {messages.length === 0 && (
            <div className="fade-up">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-full sidebar-grain flex items-center justify-center">
                  <Flag size={15} className="text-white" />
                </div>
                <div className="text-sm text-[#1C1F1E] font-medium">Good morning, Jake.</div>
              </div>
              <p className="text-sm text-[#52525B] leading-relaxed pl-11">
                I'm watching Monarch Bay live. Ask me anything about today's operations —
                revenue, pace, F&B, or where you should focus right now.
              </p>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap",
                  m.role === "user"
                    ? "bg-[#0F3F2D] text-white rounded-br-md"
                    : "bg-[#F3F4F6] text-[#1C1F1E] rounded-bl-md"
                )}
              >
                {m.content || (busy && i === messages.length - 1 ? (
                  <span className="inline-flex gap-1 items-center text-[#52525B]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0F3F2D] pulse-dot" />
                    thinking
                  </span>
                ) : "")}
              </div>
            </div>
          ))}
        </div>

        {messages.length === 0 && suggestions.length > 0 && (
          <div className="px-6 pb-3 space-y-2">
            {suggestions.map((s) => (
              <button
                key={s}
                data-testid="ask-suggestion"
                onClick={() => send(s)}
                className="w-full text-left px-4 py-2.5 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0F3F2D]/30 hover:bg-[#0F3F2D]/[0.03] transition-colors text-sm text-[#1C1F1E]"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="p-4 border-t border-[#E5E7EB] shrink-0">
          <div className="flex items-end gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 focus-within:border-[#0F3F2D]/40 transition-colors">
            <textarea
              data-testid="ask-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder="Ask about today's operations..."
              className="flex-1 resize-none bg-transparent outline-none text-sm text-[#1C1F1E] placeholder:text-[#A1A1AA] max-h-24"
            />
            <button
              data-testid="ask-send"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="w-8 h-8 rounded-lg bg-[#0F3F2D] hover:bg-[#165c42] disabled:opacity-30 text-white flex items-center justify-center transition-colors active:scale-95 shrink-0"
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
